<script setup lang="ts">
    import { computed, onMounted, reactive } from 'vue';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { AlertTypes } from '@shared/components/BsAlerts';
    import FormInput from '@shared/components/Form/FormInput.vue';
    import FormSelect from '@shared/components/Form/FormSelect.vue';
    import Loading from '@shared/components/Loading.vue';
    import { ApiError, BillingAddress, getSmsTopUpBillingAddress, saveSmsTopUpBillingAddress } from './AccountPrePaidBalance.access';

    // The billing address step of the SMS top-up (card 5.3): emits `ready` with the address once it is complete — at once when it
    // already was, after the customer fills in what is missing otherwise. A complete address is never edited here (decided
    // 2026-10-26; the server refuses it too). The address is what the invoice and its VAT use.
    // canComplete: an administrator on the card path — completing the customer's address is Account Write; others are told to ask
    // one. The invoice path never completes it (SMS top-up on invoice, R17) and says so with incompleteMessage (contact support).
    const props = defineProps<{ canComplete?: boolean; incompleteMessage?: string }>();
    const emit = defineEmits<{
        (e: 'ready', address: BillingAddress): void;
        (e: 'failed', error: ApiError | undefined): void;
    }>();

    // Mirror of the server's limits (SmsTopUpFacade: Chargebee's billing address limits); the server stays authoritative.
    const MAX_NAME_OR_LINE_LENGTH = 150;
    const MAX_CITY_LENGTH = 50;
    const MAX_ZIP_LENGTH = 20;

    // The countries Ungapped.Core's Countries offers (the server checks the same list).
    const COUNTRIES = [
        { value: 'SE', label: 'Sverige' },
        { value: 'DK', label: 'Danmark' },
        { value: 'NO', label: 'Norge' },
        { value: 'FI', label: 'Finland' },
        { value: 'FR', label: 'France' },
        { value: 'US', label: 'United States' },
    ];

    // unavailable: the address could not be read — the parent shows the error; nothing to fill in.
    const state = reactive<{ loading: boolean; unavailable: boolean; form: BillingAddress }>({
        loading: true,
        unavailable: false,
        form: { FirstName: '', LastName: '', Company: '', Line1: '', Line2: '', City: '', Zip: '', Country: 'SE' },
    });

    const formComplete = computed(() => !!state.form.Line1?.trim() && !!state.form.City?.trim() && !!state.form.Zip?.trim() && !!state.form.Country);

    onMounted(async () => {
        try {
            const current = await getSmsTopUpBillingAddress();
            if (current.Complete && current.Address) {
                emit('ready', current.Address);
                return;
            }
            // Prefill what the customer already has; only the missing fields need typing.
            const known = Object.fromEntries(Object.entries(current.Address ?? {}).filter(([, value]) => value)) as Partial<BillingAddress>;
            state.form = { ...state.form, ...known };
            state.loading = false;
        } catch (error) {
            state.loading = false;
            state.unavailable = true;
            emit('failed', error as ApiError);
        }
    });

    async function save(): Promise<void> {
        state.loading = true;
        try {
            await saveSmsTopUpBillingAddress(state.form);
            emit('ready', { ...state.form });
        } catch (error) {
            // The form again, as filled in: the parent shows the error, the customer can correct and save again or cancel.
            state.loading = false;
            emit('failed', error as ApiError);
        }
    }

    const text = (value: unknown) => String(value ?? '');
</script>

<template>
    <Loading :show="state.loading"></Loading>
    <BsAlert
        v-if="!state.loading && !state.unavailable && !props.canComplete"
        :type="AlertTypes.Info"
        :dismissible="false"
        :content="props.incompleteMessage ?? $Resources.SmsTopUpAskAdministratorForAddressMessage ?? ''" />
    <section v-else-if="!state.loading && !state.unavailable">
        <BsAlert :type="AlertTypes.Info" :dismissible="false" :content="$Resources.SmsTopUpBillingAddressMissingInfo" />
        <FormInput
            class="form-group"
            :label="$Resources.SmsTopUpFirstNameLabel"
            :modelValue="state.form.FirstName"
            :maxlength="MAX_NAME_OR_LINE_LENGTH"
            @update:modelValue="(v) => (state.form.FirstName = text(v))" />
        <FormInput
            class="form-group"
            :label="$Resources.SmsTopUpLastNameLabel"
            :modelValue="state.form.LastName"
            :maxlength="MAX_NAME_OR_LINE_LENGTH"
            @update:modelValue="(v) => (state.form.LastName = text(v))" />
        <FormInput
            class="form-group"
            :label="$Resources.SmsTopUpCompanyLabel"
            :modelValue="state.form.Company"
            :maxlength="MAX_NAME_OR_LINE_LENGTH"
            @update:modelValue="(v) => (state.form.Company = text(v))" />
        <FormInput
            class="form-group"
            :label="$Resources.SmsTopUpAddressLine1Label"
            :modelValue="state.form.Line1"
            :maxlength="MAX_NAME_OR_LINE_LENGTH"
            :required="true"
            @update:modelValue="(v) => (state.form.Line1 = text(v))" />
        <FormInput
            class="form-group"
            :label="$Resources.SmsTopUpAddressLine2Label"
            :modelValue="state.form.Line2"
            :maxlength="MAX_NAME_OR_LINE_LENGTH"
            @update:modelValue="(v) => (state.form.Line2 = text(v))" />
        <FormInput
            class="form-group"
            :label="$Resources.SmsTopUpZipLabel"
            :modelValue="state.form.Zip"
            :maxlength="MAX_ZIP_LENGTH"
            :required="true"
            @update:modelValue="(v) => (state.form.Zip = text(v))" />
        <FormInput
            class="form-group"
            :label="$Resources.SmsTopUpCityLabel"
            :modelValue="state.form.City"
            :maxlength="MAX_CITY_LENGTH"
            :required="true"
            @update:modelValue="(v) => (state.form.City = text(v))" />
        <FormSelect
            class="form-group"
            :label="$Resources.SmsTopUpCountryLabel"
            :options="COUNTRIES"
            :modelValue="state.form.Country"
            :required="true"
            @update:modelValue="(v) => (state.form.Country = text(v))" />
        <div class="text-right">
            <button class="btn btn-primary" :disabled="!formComplete" @click="save">{{ $Resources.SmsTopUpSaveAddressBtn }}</button>
        </div>
    </section>
</template>
